import { useEffect, useMemo, useState } from 'react';
import { db } from '../../database/firebase';
import {
  fetchSaleReportBundle,
  buildSalesDataGrid,
  buildCouponsData,
  buildRemainingPayments,
  computeOrganisationOrVenueSales,
  resolveEventOwners,
  fetchOwnerLinkedLookups,
  buildOwnershipSalesBreakdown,
  computeSettlementNet,
} from '../../services/salesReportService';

export const useSaleReport = ({ eventId, stockId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [event, setEvent] = useState(null);
  const [stock, setStock] = useState(null);
  const [stocks, setStocks] = useState([]);
  const [salesDocs, setSalesDocs] = useState([]);
  const [eventOwners, setEventOwners] = useState([]);
  const [ownershipLookups, setOwnershipLookups] = useState({
    promoterToOwner: new Map(),
  });

  const [salesGrid, setSalesGrid] = useState([]);
  const [couponsGrid, setCouponsGrid] = useState([]);
  const [remainingGrid, setRemainingGrid] = useState([]);
  const [refunds, setRefunds] = useState(null);

  /** Tek oturum seçiliyse kupon/settlement etkinlik geneli — payable'a karıştırma */
  const isSessionScoped = Boolean(stockId);

  useEffect(() => {
    let isMounted = true;
    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const bundle = await fetchSaleReportBundle(db, eventId, stockId || undefined);
        const ev = bundle.event;
        if (!ev) throw new Error('Event not found');
        const stocksList = Array.isArray(bundle.stocks) ? bundle.stocks : [];
        const st = bundle.stock || stocksList[0] || null;
        const docs = bundle.sales || [];
        const catalog = stockId ? st || stocksList : stocksList.length ? stocksList : st;

        if (!isMounted) return;

        // Önce grid'leri göster — ownership arka planda
        const { grid: salesGridLocal } = buildSalesDataGrid(ev, catalog, docs);
        const coupons = isSessionScoped ? [] : buildCouponsData(ev);
        const remaining = buildRemainingPayments(catalog, docs);

        setEvent(ev);
        setStock(st);
        setStocks(stocksList);
        setSalesDocs(docs);
        setSalesGrid(salesGridLocal);
        setCouponsGrid(coupons);
        setRemainingGrid(remaining);
        setRefunds(bundle.refunds || null);
        setLoading(false);

        // Ownership zenginleştirme (yavaş lookup'lar) — UI'yi bloklamaz
        try {
          const owners = await resolveEventOwners(ev, db);
          if (!isMounted) return;
          setEventOwners(owners);
          const lookups = await fetchOwnerLinkedLookups(owners, docs);
          if (!isMounted) return;
          setOwnershipLookups(lookups);
        } catch {
          /* ownership optional */
        }
      } catch (e) {
        if (!isMounted) return;
        setError(e?.message || 'Failed to load sale report');
        setLoading(false);
      }
    };
    run();
    return () => {
      isMounted = false;
    };
  }, [eventId, stockId, isSessionScoped]);

  const ownershipBreakdown = useMemo(
    () => buildOwnershipSalesBreakdown(event, salesDocs, eventOwners, ownershipLookups),
    [event, salesDocs, eventOwners, ownershipLookups],
  );

  const totals = useMemo(() => {
    const totalProductSales = salesGrid.reduce(
      (sum, row) => sum + (row.id !== 'total' ? (row.totalSale ?? row.sold * row.price) : 0),
      0,
    );
    const totalCouponDiscount = isSessionScoped
      ? 0
      : couponsGrid.reduce(
          (sum, row) => sum + (row.id !== 'total' ? row.totalDiscount || 0 : 0),
          0,
        );
    const totalRemainingAmount = remainingGrid.reduce((sum, row) => {
      if (row.id === 'total') return sum;
      if (row.totalRmainingAmount != null) return sum + Number(row.totalRmainingAmount || 0);
      const remainingPerUnit = Number(row.price || 0) - Number(row.downPaymentAmount || 0);
      return sum + remainingPerUnit * Number(row.sold || 0);
    }, 0);
    const orgVenueSales = computeOrganisationOrVenueSales(event, salesDocs);
    const totalRevenue = ownershipBreakdown.totalRevenue || 0;
    const settlementNet = isSessionScoped ? 0 : computeSettlementNet(event?.settlementEntries);

    // Ana satış toplamı: hem ürün gridi hem de toplam ciro eşleşir.
    const baseTotalSales = totalRevenue > 0 ? totalRevenue : totalProductSales;
    const partialAmountRefunded = Number(refunds?.partialAmountRefunded || 0);
    const totalRefunded = Number(refunds?.totalRefunded || 0);

    // Gişe Kıbrıs Komisyon Tabanı: Sadece Gişe Kıbrıs kanallarından (web/mobil/gişe) yapılan net satışlardan hesaplanır.
    // Organizatörün elden/nakit (ocm, op, vm, vp) satışlarından komisyon alınmaz.
    const giseSalesBase = Math.max(
      0,
      baseTotalSales - totalRefunded - orgVenueSales - totalCouponDiscount - totalRemainingAmount,
    );

    const isCommissionExtra = event?.isCommissionExtra ? true : false;
    let totalCommissionAmount = 0;
    if (!isCommissionExtra) {
      totalCommissionAmount = (giseSalesBase * (event?.commissionFee || 0)) / 100;
    } else {
      totalCommissionAmount = giseSalesBase * (1 - 100 / (100 + (event?.commissionFee || 0)));
    }
    const subTotal = giseSalesBase;
    const totalPayable = subTotal - totalCommissionAmount + settlementNet;

    return {
      totalRevenue: baseTotalSales,
      totalProductSales: baseTotalSales,
      totalCouponDiscount,
      totalRemainingAmount,
      orgVenueSales,
      totalRefunded,
      partialAmountRefunded,
      fullyRefundedSales: Number(refunds?.fullyRefundedSales || 0),
      partialAmountRefundRows: Array.isArray(refunds?.partialRows) ? refunds.partialRows : [],
      subTotal,
      totalCommissionAmount,
      settlementNet,
      totalPayable,
      isSessionScoped,
    };
  }, [
    salesGrid,
    couponsGrid,
    remainingGrid,
    event,
    salesDocs,
    ownershipBreakdown,
    isSessionScoped,
    refunds,
  ]);

  const setEventData = (next) => setEvent(next);

  return {
    loading,
    error,
    event,
    setEvent: setEventData,
    stock,
    stocks,
    salesGrid,
    couponsGrid,
    remainingGrid,
    totals,
    ownershipBreakdown,
    isSeatedEvent: !!(event && event.type === 3),
    isSessionScoped,
  };
};

export default useSaleReport;
